import React, { Suspense } from 'react';
import { Navigate, redirect } from 'react-router-dom';
import type { LoaderFunction } from 'react-router-dom';
import AdminLayout from '../layouts/AdminLayout';
import ProtectedLayout from '../layouts/ProtectedLayout';
import { Permission } from '../shared/enum/permission.enum';
import { useUserStore } from '../shared/store/userStore';
import { getAuthToken } from '../shared/utils/cookie';

// 로딩 컴포넌트
function PageLoading() {
  return (
    <div className='flex items-center justify-center h-64'>
      <div className='text-lg'>페이지를 불러오는 중...</div>
    </div>
  );
}

// Lazy load 컴포넌트들
const HomePage = React.lazy(() => import('../pages/HomePage'));
const MagazineTab = React.lazy(
  () => import('../pages/admin/magazine/components/MagazineTab')
);
const MagazineForm = React.lazy(
  () => import('../pages/admin/magazine/components/form/MagazineForm')
);
const GnbTab = React.lazy(() => import('../pages/admin/gnb/GnbTab'));
const GnbForm = React.lazy(
  () => import('../pages/admin/gnb/components/form/GnbForm')
);
const AppPushTab = React.lazy(
  () => import('../pages/admin/app-push/AppPushTab')
);
const SpotTimeTab = React.lazy(
  () => import('../pages/admin/spot-time/SpotTimeTab')
);
const PickRoundTab = React.lazy(
  () => import('../pages/admin/pick-round/PickRoundTab')
);
const DashboardTab = React.lazy(
  () => import('../pages/admin/dashboard/DashboardTab')
);
const PartnerTab = React.lazy(
  () => import('../pages/admin/partner/PartnerTab')
);
const PartnerForm = React.lazy(
  () => import('../pages/admin/partner/components/form/PartnerForm')
);
const ProductTab = React.lazy(
  () => import('../pages/admin/product/ProductTab')
);
const ProductForm = React.lazy(
  () => import('../pages/admin/product/components/form/ProductForm')
);
const ProductReviewListPage = React.lazy(
  () => import('../pages/admin/product/components/table/ReviewTable')
);
const SettlementTab = React.lazy(
  () => import('../pages/admin/settlement/SettlementTab')
);
const IdusTab = React.lazy(() => import('../pages/admin/idus/IdusTab'));
const IdusProductList = React.lazy(
  () => import('../pages/admin/idus/components/product/ProductList')
);
const Dashboard = React.lazy(
  () => import('../pages/admin/idus/components/dashboard/DashboardPage')
);
const SellerList = React.lazy(
  () => import('../pages/admin/idus/components/seller/SellerList')
);
const SellerDetail = React.lazy(
  () => import('../pages/admin/idus/components/seller/SellerDetail')
);
const SellerForm = React.lazy(
  () => import('../pages/admin/idus/components/seller/SellerForm')
);
const IdusProductDetail = React.lazy(
  () => import('../pages/admin/idus/components/product/ProductDetail')
);
const IdusProductForm = React.lazy(
  () => import('../pages/admin/idus/components/product/ProductForm')
);
const BrandTab = React.lazy(() => import('../pages/admin/brand/BrandTab'));
const BrandForm = React.lazy(() => import('../pages/admin/brand/BrandForm'));
const MileageTab = React.lazy(
  () => import('../pages/admin/mileage/MileageTab')
);
const CouponTab = React.lazy(() => import('../pages/admin/coupon/CouponTab'));
const CouponForm = React.lazy(
  () => import('../pages/admin/coupon/components/form/CouponForm')
);
const CouponBookTab = React.lazy(
  () => import('../pages/admin/coupon-book/CouponBookTab')
);
const UserManagementPage = React.lazy(
  () => import('../pages/user-management/UserManagementPage')
);
const EmailTemplatePage = React.lazy(
  () => import('../pages/email-template/EmailTemplatePage')
);
const ReferralTab = React.lazy(
  () => import('../pages/admin/referral/ReferralTab')
);
const CloudSchedulerTab = React.lazy(
  () => import('../pages/admin/cloud-scheduler/CloudSchedulerTab')
);
const HomeBannerTab = React.lazy(
  () => import('../pages/admin/home-banner/HomeBannerTab')
);
const DeepLinkTab = React.lazy(
  () => import('../pages/admin/deeplink/DeepLinkTab')
);
const EventTab = React.lazy(() => import('../pages/admin/events/EventTab'));
const OrderTab = React.lazy(
  () => import('../pages/admin/order/components/OrderTab')
);
const EventDisplayTab = React.lazy(
  () => import('../pages/admin/event-display/EventDisplayTab')
);
const EventDisplayComponentForm = React.lazy(
  () =>
    import(
      '../pages/admin/event-display/components/form/EventDisplayComponentForm'
    )
);
// Helper function to wrap lazy components with Suspense
const createLazyElement = (
  LazyComponent: React.LazyExoticComponent<React.ComponentType<any>>,
  props?: any
) =>
  React.createElement(
    Suspense,
    { fallback: React.createElement(PageLoading) },
    React.createElement(LazyComponent, props)
  );

// Auth loader(로그인 되어 있지 않으면 로그인 페이지로 이동)
export const protectedLoader: LoaderFunction = () => {
  const { accessToken } = useUserStore.getState();
  const cookieToken = getAuthToken();

  if (!accessToken && !cookieToken) {
    return redirect('/login');
  }
  return null;
};

// Permission loader(권한 체크 로더) - 비동기로 변경
export const permissionLoader =
  (requiredPermissions: Permission[]) => async () => {
    const { user, accessToken } = useUserStore.getState();
    const cookieToken = getAuthToken();

    // 토큰이 없으면 로그인 페이지로
    if (!accessToken && !cookieToken) {
      return redirect('/login');
    }

    // 사용자 정보나 권한이 없으면 잠시 기다림 (useCookieSync가 실행 중일 수 있음)
    if (!user || !user.permissions || user.permissions.length === 0) {
      console.log('[permissionLoader] 권한 정보 로딩 중... 잠시 대기');

      // 최대 3초 동안 권한 정보가 로드되기를 기다림
      let attempts = 0;
      const maxAttempts = 30; // 100ms * 30 = 3초

      while (attempts < maxAttempts) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, 100);
        });
        const currentState = useUserStore.getState();

        if (
          currentState.user?.permissions &&
          currentState.user.permissions.length > 0
        ) {
          console.log('[permissionLoader] 권한 정보 로드 완료');
          break;
        }

        attempts++;
      }

      // 여전히 권한이 없으면 관리자인지 체크 (개발팀 등)
      const finalState = useUserStore.getState();
      if (
        !finalState.user?.permissions ||
        finalState.user.permissions.length === 0
      ) {
        // 관리자 역할인지 확인
        const isAdmin = finalState.user?.roles?.some((role: any) => {
          const roleName = typeof role === 'string' ? role : role?.name;
          return ['admin', 'developer', 'ceo'].includes(
            roleName?.toLowerCase()
          );
        });

        if (!isAdmin) {
          console.warn('[permissionLoader] 권한 정보 로드 실패 또는 권한 없음');
          return redirect('/');
        } else {
          console.log('[permissionLoader] 관리자 역할로 접근 허용');
          return null; // 관리자는 권한 체크를 건너뛰고 바로 허용
        }
      }
    }

    // 현재 사용자 권한 가져오기
    const currentUser = useUserStore.getState().user;
    const userPermissions = currentUser?.permissions || [];

    console.log(
      `[permissionLoader] 권한 체크: 필요권한=${requiredPermissions.length}개, 보유권한=${userPermissions.length}개`
    );

    // 관리자인지 먼저 체크 (관리자는 모든 권한 허용)
    const isAdmin = currentUser?.roles?.some((role: any) => {
      const roleName = typeof role === 'string' ? role : role?.name;
      return ['admin', 'developer', 'ceo'].includes(roleName?.toLowerCase());
    });

    if (isAdmin) {
      console.log('[permissionLoader] 관리자 역할로 모든 권한 허용');
      return null;
    }

    // 권한이 객체 배열인지 문자열 배열인지 확인 후 적절히 처리
    const hasPermission = requiredPermissions.every((requiredPerm) => {
      const hasThisPermission = userPermissions.some((userPerm: any) => {
        // 권한이 객체인 경우 name 필드 확인
        if (userPerm && typeof userPerm === 'object' && userPerm.name) {
          return userPerm.name === requiredPerm;
        }
        // 권한이 문자열인 경우 직접 비교
        if (typeof userPerm === 'string') {
          return userPerm === requiredPerm;
        }
        return false;
      });

      console.log(
        `[permissionLoader] ${requiredPerm}: ${hasThisPermission ? '✅' : '❌'}`
      );
      return hasThisPermission;
    });

    if (!hasPermission) {
      console.warn('[permissionLoader] 권한 부족으로 홈으로 리다이렉트');
      return redirect('/');
    }

    console.log('[permissionLoader] 권한 체크 통과');
    return null;
  };

export const protectedRoutes = {
  element: React.createElement(ProtectedLayout),
  loader: protectedLoader,
  children: [
    {
      path: 'home',
      element: createLazyElement(HomePage),
    },
    {
      path: 'admin',
      element: React.createElement(AdminLayout),
      children: [
        {
          path: 'partner',
          children: [
            {
              index: true,
              element: createLazyElement(PartnerTab),
            },
            {
              path: 'create',
              element: createLazyElement(PartnerForm),
            },
            {
              path: ':id/edit',
              element: createLazyElement(PartnerForm),
            },
          ],
        },
        {
          path: 'product',
          children: [
            {
              index: true,
              element: createLazyElement(ProductTab),
            },
            {
              path: ':id/review',
              element: createLazyElement(ProductReviewListPage),
            },
            {
              path: 'create',
              element: createLazyElement(ProductForm),
            },
            {
              path: ':id/edit',
              element: createLazyElement(ProductForm),
            },
          ],
        },
        {
          path: 'dashboard',
          element: createLazyElement(DashboardTab),
        },
        {
          path: 'magazine-management',
          children: [
            {
              index: true,
              element: createLazyElement(MagazineTab),
            },
            {
              path: 'create',
              element: createLazyElement(MagazineForm),
            },
            {
              path: ':id/edit',
              element: createLazyElement(MagazineForm, {
                isEditMode: true,
              }),
            },
          ],
        },
        {
          path: 'events',
          element: createLazyElement(EventTab),
        },
        {
          path: 'event-display',
          children: [
            {
              index: true,
              element: createLazyElement(EventDisplayTab),
            },
            {
              path: 'component/:groupId/create',
              element: createLazyElement(EventDisplayComponentForm),
            },
            {
              path: 'component/:groupId/:id/edit',
              element: createLazyElement(EventDisplayComponentForm, {
                isEditMode: true,
              }),
            },
          ],
        },
        {
          path: 'app-push',
          element: createLazyElement(AppPushTab),
        },
        {
          path: 'spot-time',
          element: createLazyElement(SpotTimeTab),
        },
        {
          path: 'pick-round',
          element: createLazyElement(PickRoundTab),
        },
        {
          path: 'order',
          element: createLazyElement(OrderTab),
        },
        {
          path: 'settlement',
          element: createLazyElement(SettlementTab),
        },
        {
          path: 'referral',
          element: createLazyElement(ReferralTab),
        },
        {
          path: 'cloud-scheduler',
          element: createLazyElement(CloudSchedulerTab),
        },
        {
          path: 'home-banner',
          element: createLazyElement(HomeBannerTab),
        },
        {
          path: 'deeplink',
          element: createLazyElement(DeepLinkTab),
        },
        {
          path: 'coupon',
          children: [
            {
              index: true,
              element: createLazyElement(CouponTab),
            },
            {
              path: 'create',
              element: createLazyElement(CouponForm),
            },
            {
              path: ':id/edit',
              element: createLazyElement(CouponForm, {
                isEditMode: true,
              }),
            },
          ],
        },
        {
          path: 'gnb',
          children: [
            {
              index: true,
              element: createLazyElement(GnbTab),
            },
            {
              path: 'create',
              element: createLazyElement(GnbForm),
            },
            {
              path: ':id/edit',
              element: createLazyElement(GnbForm, {
                isEditMode: true,
              }),
            },
          ],
        },
        {
          path: 'mileage',
          element: createLazyElement(MileageTab),
        },
        {
          path: 'coupon-book',
          element: createLazyElement(CouponBookTab),
        },
        {
          path: 'idus',
          element: createLazyElement(IdusTab),
          children: [
            {
              index: true,
              element: React.createElement(Navigate, { to: 'dashboard' }),
            },
            {
              path: 'dashboard',
              element: createLazyElement(Dashboard),
            },
            {
              path: 'sellers',
              children: [
                {
                  index: true,
                  element: createLazyElement(SellerList),
                },
                {
                  path: 'new',
                  element: createLazyElement(SellerForm),
                },
                {
                  path: ':id',
                  element: createLazyElement(SellerDetail),
                },
                {
                  path: ':id/edit',
                  element: createLazyElement(SellerForm, {
                    isEditMode: true,
                  }),
                },
              ],
            },
            {
              path: 'products',
              children: [
                {
                  index: true,
                  element: createLazyElement(IdusProductList),
                },
                {
                  path: 'new',
                  element: createLazyElement(IdusProductForm),
                },
                {
                  path: ':id',
                  element: createLazyElement(IdusProductDetail),
                },
                {
                  path: ':id/edit',
                  element: createLazyElement(IdusProductForm, {
                    isEditMode: true,
                  }),
                },
              ],
            },
          ],
        },
        {
          path: 'brand',
          children: [
            {
              index: true,
              element: createLazyElement(BrandTab),
            },
            {
              path: 'create',
              element: createLazyElement(BrandForm),
            },
            {
              path: 'edit',
              element: createLazyElement(BrandForm),
            },
          ],
        },
        // {
        //   path: 'partner-dashboard',
        //   element: React.createElement(PartnerDashboardPage),
        // },
        // {
        //   path: 'partner-products',
        //   element: React.createElement(PartnerProductsPage),
        // },
        // {
        //   path: 'partner-settlements',
        //   element: React.createElement(PartnerSettlementsPage),
        // },
        // {
        //   path: 'order-form',
        //   element: React.createElement(OrderFormTab),
        // },
      ],
    },
    {
      path: 'user-management',
      element: createLazyElement(UserManagementPage),
    },
    {
      path: 'email-template',
      element: createLazyElement(EmailTemplatePage),
    },
    {
      path: '',
      element: React.createElement(Navigate, {
        to: 'home',
        replace: true,
      }),
    },
  ],
};
